import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuote } from '@features/quote/context/QuoteContext';
import { getProductCurrency } from '@/features/products/services/productCurrency';
import { deriveVehicleScheduleContext } from '@/app/utils/vehicleScheduleContext';
import type { ConfiguredService } from '../types/service.types';
import type { SmriqPlanSettings } from '../types/smriq.types';
import { useSmriqConfirmedVehicleSync } from './useSmriqConfirmedVehicleSync';
import { useSmriqContext } from './useSmriqContext';
import { useSmriqJourney, type GeneratePlanInput } from './useSmriqJourney';
import { useSmriqLastCompletedService } from './useSmriqLastCompletedService';
import { useSmriqDeclineRegenerate } from './useSmriqDeclineRegenerate';
import { useSmriqDeclines } from './useSmriqDeclines';
import { useSmriqInclusionsByVisit } from './useSmriqInclusionsByVisit';
import { useSmriqGeneratedPlan } from './useSmriqGeneratedPlan';
import { useSmriqAutoConfirm } from './useSmriqAutoConfirm';
import { useSmriqPlanFieldsDraft } from './useSmriqPlanFieldsDraft';
import { useSmriqLastServiceEdit } from './useSmriqLastServiceEdit';
import { useSmriqAutoGenerate } from './useSmriqAutoGenerate';
import { isAutoMatch } from '../utils/isAutoMatch';
import { resolveVrm } from '../utils/resolveVrm';
import { isSmriqSessionExpired } from '../utils/smriqSessionExpired';
import { resolvePlanSettingsDefaults } from '../utils/resolvePlanSettingsDefaults';
import { resolveSmriqServiceCountBounds } from '../utils/smriqServiceCountBounds';

// A burst of include/decline toggles settles into one regenerate rather than one per click.
// Long enough to toggle several before the plan re-prices, short enough to feel responsive.
export const AUTO_REGENERATE_DEBOUNCE_MS = 1500;

interface UseSmriqConfiguratorJourneyArgs {
    setConfiguredServices: React.Dispatch<React.SetStateAction<ConfiguredService[]>>;
    autoRegenerateDebounceMs?: number;
}

/** Owns the SMRiQ configurator's journey: auto-confirm, auto-generate, persisted-settings
 *  restore, debounced regeneration, decline hydration and the edit/restart state. */
export function useSmriqConfiguratorJourney({
    setConfiguredServices,
    autoRegenerateDebounceMs = AUTO_REGENERATE_DEBOUNCE_MS,
}: UseSmriqConfiguratorJourneyArgs) {
    const {
        selectedProduct,
        selectedVehicle,
        syncSmriqConfirmedMileage,
        servicePlanSessionId,
        setServicePlanSessionId,
        smriqConfirmedVehicle,
        setSmriqConfirmedVehicle,
        smriqPlanSettings,
        setSmriqPlanSettings,
        configuredServices,
        clearPaymentSelection,
    } = useQuote();

    const currency = getProductCurrency(selectedProduct);
    const {
        data: context,
        isLoading: isContextLoading,
        refetch: refetchContext,
    } = useSmriqContext(selectedProduct?.id);

    const vehicleContext = useMemo(
        () => deriveVehicleScheduleContext(selectedVehicle),
        [selectedVehicle],
    );
    const defaultVrm = resolveVrm(selectedVehicle) ?? '';

    // Only the snapshot taken against the live session describes what `generate`
    // prices; any other belongs to a session this quote no longer holds.
    const confirmedVehicle =
        smriqConfirmedVehicle && smriqConfirmedVehicle.sessionId === servicePlanSessionId
            ? smriqConfirmedVehicle
            : null;

    const declines = useSmriqDeclines();
    const { toggleDecline, isDeclineAllowed, atExclusionLimit, reconcile, exclusions } = declines;
    const { generatedServices, pricingVariant, handleGenerated } = useSmriqGeneratedPlan(reconcile);

    const handleConfirmedVehicle = useSmriqConfirmedVehicleSync({
        selectedVehicle,
        setSmriqConfirmedVehicle,
        syncSmriqConfirmedMileage,
    });

    const {
        confirmVehicle,
        isConfirming,
        confirmError,
        confirmed,
        preview,
        isPreviewLoading,
        previewError,
        previewSettled,
        matchedServiceOptionId,
        retryPreview,
        generatePlan,
        isGenerating,
        generateError,
        isStale,
        planControlsStale,
        planSettingsKnown,
        markStale,
        markSelectionStale,
        needsReconfirm,
        markVehicleDirty,
        discardEdit,
        restart,
    } = useSmriqJourney({
        productId: selectedProduct?.id,
        sessionId: servicePlanSessionId ?? null,
        confirmedVehicle,
        onSessionId: setServicePlanSessionId,
        onConfirmedVehicle: handleConfirmedVehicle,
        // A schedule change invalidates any payment selection confirmed against it.
        onServices: (services) => {
            clearPaymentSelection();
            setConfiguredServices(services);
        },
        onGeneratedServices: handleGenerated,
        // Committed here rather than when Update is pressed, so a rejected generate cannot
        // leave the quote claiming settings the schedule it still holds was never built from.
        onPlanSettings: setSmriqPlanSettings,
        serviceCountBounds: resolveSmriqServiceCountBounds(selectedProduct),
    });

    const hasPlan = configuredServices.length > 0;
    const sessionExpired = isSmriqSessionExpired(generateError);
    // Unsupported only when explicitly published false; an unknown capability
    // leaves the decline controls offered.
    const exclusionsSupported =
        context?.capabilities.supports_recommended_operation_exclusions !== false;

    const inclusionsByVisit = useSmriqInclusionsByVisit({
        generatedServices,
        configuredServices,
        reconcile,
    });

    // The settings the plan was generated with live in quote state (redux-persisted), so a
    // debounced toggle can regenerate after a remount. A ref mirrors them for the callbacks.
    const settingsRef = useRef<SmriqPlanSettings | null | undefined>(smriqPlanSettings);
    settingsRef.current = smriqPlanSettings;

    const lastCompleted = useSmriqLastCompletedService({
        preview,
        isPreviewLoading,
        previewError,
        savedSelection: smriqPlanSettings?.last_completed_service,
        savedSettings: smriqPlanSettings,
        previewSettled,
        sessionId: servicePlanSessionId ?? null,
        canResumePreview: !!confirmedVehicle && !needsReconfirm,
        retryPreview,
        supported: context?.capabilities.supports_last_completed_service !== false,
        markStale: markSelectionStale,
        vehicleLastServiceDate: vehicleContext.lastServiceDate,
        vehicleLastServiceMileage: vehicleContext.lastServiceMileage,
    });

    const { selection, generateAllowed, autoGenerateReady, revertSelection } = lastCompleted;
    const autoMatchedService =
        isAutoMatch(selection) && !isStale
            ? (lastCompleted.options.find((option) => option.id === matchedServiceOptionId) ?? null)
            : null;

    const { canAutoConfirm, resetAutoConfirm } = useSmriqAutoConfirm({
        productId: selectedProduct?.id,
        selectedVehicle,
        defaultVrm,
        vehicleContext,
        servicePlanSessionId,
        isConfirming,
        confirmError,
        confirmVehicle,
    });

    const [editRequested, setEditRequested] = useState(false);
    const [editPlanRequested, setEditPlanRequested] = useState(false);
    // The form is only for what auto-confirm cannot silently cover: a vehicle missing
    // mileage, a rejected confirm, a session resumed unverified, or an explicit edit.
    const showVehicleForm =
        needsReconfirm || editRequested || (!confirmed && (!canAutoConfirm || !!confirmError));
    const autoConfirmPending = canAutoConfirm && !confirmed && !confirmError && !showVehicleForm;

    const {
        retrievingPlan,
        planLoading,
        planSettingsKnownOrRestored,
        showPlanControls,
        planFieldsEdited,
        planStartDraft,
        handlePlanFieldsChange,
        handlePlanStartDateChange,
        clearPlanFieldsEdited,
    } = useSmriqPlanFieldsDraft(markStale, {
        hasContext: !!context,
        contextDurationDefault: context?.duration.default,
        contextMotDefault: context?.mot.default,
        isContextLoading,
        canAutoConfirm,
        confirmed,
        configuredServiceCount: configuredServices.length,
        planSettingsKnown,
        hasPersistedPlanSettings: !!smriqPlanSettings,
        needsReconfirm,
        confirmError,
        generateError,
        showVehicleForm,
        isGenerating,
        hasPlan,
        editPlanRequested,
        isStale: planControlsStale,
        generationBlocked: !generateAllowed,
    });

    const decline = useSmriqDeclineRegenerate({
        settingsRef,
        exclusions,
        exclusionsSupported,
        lastCompleted,
        isGenerating,
        needsReconfirm,
        sessionExpired,
        planFieldsEdited,
        generatePlan,
        toggleDecline,
        markStale,
        debounceMs: autoRegenerateDebounceMs,
    });
    const { handleToggleDecline, cancelPending, markApplied, resumeInterrupted } = decline;

    const handleGenerate = useCallback(
        (payload: SmriqPlanSettings) => {
            if (!generateAllowed) return;
            cancelPending();
            markApplied();
            // Only the plan-controls form picks a start date; other generates keep the saved one.
            const planStartDate = payload.plan_start_date ?? settingsRef.current?.plan_start_date;
            const input: GeneratePlanInput = {
                ...payload,
                last_completed_service: selection,
                ...(planStartDate ? { plan_start_date: planStartDate } : {}),
                ...(exclusionsSupported ? { recommended_operation_exclusions: exclusions } : {}),
            };
            generatePlan(input);
        },
        [generatePlan, exclusions, exclusionsSupported, generateAllowed, selection, cancelPending, markApplied],
    );

    const lastSnapshotRef = useRef(smriqConfirmedVehicle);
    const lastServicesRef = useRef(configuredServices);

    // A successful confirm re-snapshots the vehicle (only onSuccess sets it); that new
    // reference collapses any open edit form back to the summary.
    useEffect(() => {
        if (smriqConfirmedVehicle !== lastSnapshotRef.current) {
            lastSnapshotRef.current = smriqConfirmedVehicle;
            setEditRequested(false);
        }
    }, [smriqConfirmedVehicle]);

    // A fresh generate (new configuredServices reference) collapses the plan controls back
    // to the summary; clicking Edit plan alone leaves them open until the next generate.
    useEffect(() => {
        if (configuredServices !== lastServicesRef.current) {
            lastServicesRef.current = configuredServices;
            setEditPlanRequested(false);
            clearPlanFieldsEdited();
        }
    }, [configuredServices, clearPlanFieldsEdited]);

    const handleRestart = useCallback(() => {
        resetAutoConfirm();
        setEditRequested(false);
        clearPlanFieldsEdited();
        cancelPending();
        revertSelection();
        restart();
    }, [restart, resetAutoConfirm, cancelPending, revertSelection, clearPlanFieldsEdited]);

    // Closes an explicit vehicle edit without changing anything, back to the confirmed summary.
    const handleCancelVehicleEdit = useCallback(() => {
        setEditRequested(false);
        revertSelection();
        discardEdit();
        resumeInterrupted();
    }, [discardEdit, revertSelection, resumeInterrupted]);

    // Closes an explicit plan-controls edit without changing anything, back to the plan summary.
    const handleCancelPlanEdit = useCallback(() => {
        setEditPlanRequested(false);
        clearPlanFieldsEdited();
        revertSelection();
        discardEdit();
        resumeInterrupted();
    }, [discardEdit, revertSelection, clearPlanFieldsEdited, resumeInterrupted]);

    const lastServiceEdit = useSmriqLastServiceEdit({
        configuredServices,
        lastCompleted,
        generateError,
        cancellable: hasPlan && !needsReconfirm && !generateError,
        planFieldsEdited,
        revertSelection,
        discardEdit,
        resumeInterrupted,
    });

    useSmriqAutoGenerate({
        settingsRef,
        context,
        confirmed,
        needsReconfirm,
        isGenerating,
        autoGenerateReady,
        planFieldsEdited,
        hasPlan,
        generate: handleGenerate,
        // A reconfirm minted a replacement session and cleared the plan, so rebuild it once more.
        rearmKey: smriqConfirmedVehicle,
    });

    const updateSettings = resolvePlanSettingsDefaults(smriqPlanSettings, context);

    return {
        context,
        isContextLoading,
        refetchContext,
        currency,
        configuredServices,
        smriqPlanSettings,
        vehicleContext,
        defaultVrm,
        confirmedVehicle,

        confirmVehicle,
        isConfirming,
        confirmError,
        confirmed,
        needsReconfirm,
        markVehicleDirty,
        markStale,
        handlePlanFieldsChange,
        handlePlanStartDateChange,
        isStale,
        isGenerating,
        generateError,
        hasPlan,
        planSettingsKnownOrRestored,

        showVehicleForm,
        autoConfirmPending,
        retrievingPlan,
        planLoading,
        showPlanControls,
        // The reg is fixed by the quote vehicle; only offer to type it when none reached
        // this page (changed on the vehicle details page in the normal case).
        allowRegistrationEdit: defaultVrm === '',
        requestVehicleEdit: () => setEditRequested(true),
        requestPlanEdit: () => setEditPlanRequested(true),
        // Cancel only when there is a settled state to return to; an initial or unverified
        // confirm, or a required regenerate, must be completed rather than dismissed.
        cancelVehicleEdit: editRequested && confirmed ? handleCancelVehicleEdit : undefined,
        cancelPlanEdit:
            editPlanRequested &&
            hasPlan &&
            planSettingsKnownOrRestored &&
            !needsReconfirm &&
            !generateError
                ? handleCancelPlanEdit
                : undefined,

        handleGenerate,
        handleRestart,

        lastCompletedService: {
            ...lastCompleted,
            autoMatchedService,
            isPreviewLoading,
            planFieldsEdited,
            // While a draft is pending only the plan-controls Update sends, and it sends the draft.
            planStartDate: planStartDraft ?? smriqPlanSettings?.plan_start_date,
            changeSelection: decline.handleSelectionChange,
            onUpdate: updateSettings ? () => handleGenerate(updateSettings) : undefined,
            ...lastServiceEdit,
        },

        inclusionsByVisit,
        pricingVariantDescription: pricingVariant?.description ?? null,
        isItemDeclined: declines.isDeclined,
        handleToggleDecline,
        isDeclineAllowed,
        atExclusionLimit,
        exclusionsSupported,
        disableToggles: decline.disableToggles,
    };
}
